import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  BarChart3,
  BookOpen,
  Users,
  TrendingUp,
  LogOut,
  Library,
  User,
  History,
  Scale,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import useIsAdmin from "@/hooks/useIsAdmin";
import { ThemeToggle } from "@/components/shared/ui/theme-toggle";
import { LanguageSelector } from "@/components/shared/ui/language-selector";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/shared/ui/sidebar";

export function AppSidebar() {
  const { profile, signOut } = useAuth();
  const { label: roleLabel, isTherapist, isClient } = useRole();
  const { isAdmin } = useIsAdmin();
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = (href: string) => {
    // Exact match for overview/home routes
    if (
      href === "/dashboard/therapist/overview" ||
      href === "/dashboard/client/journey"
    ) {
      return location.pathname === href;
    }
    // Prefix match for sub-routes
    return location.pathname.startsWith(href);
  };

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      {/* Header: brand + role */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip="HealJou">
              <Link to="/">
                <div className="flex aspect-square size-8 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground text-sm font-bold tracking-tight shadow-sm">
                  H
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold tracking-tight">
                    {t("navigation.title")}
                  </span>
                  <span className="text-[11px] uppercase tracking-widest text-sidebar-foreground/50">
                    {roleLabel ?? profile?.role}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* ── Therapist Navigation ── */}
        {isTherapist && (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>
                {t("navigation.workspace", "Workspace")}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive("/dashboard/therapist/overview")}
                      tooltip={t("navigation.home", "Home")}
                    >
                      <Link to="/dashboard/therapist/overview">
                        <Home className="size-4" />
                        <span>{t("navigation.home", "Home")}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(
                        "/dashboard/therapist/journey-builder"
                      )}
                      tooltip={t(
                        "navigation.journeyBuilder",
                        "Journey builder"
                      )}
                    >
                      <Link to="/dashboard/therapist/journey-builder">
                        <BarChart3 className="size-4" />
                        <span>
                          {t("navigation.journeyBuilder", "Journey builder")}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive("/dashboard/therapist/templates")}
                      tooltip={t("navigation.templates", "Templates")}
                    >
                      <Link to="/dashboard/therapist/templates">
                        <BookOpen className="size-4" />
                        <span>{t("navigation.templates", "Templates")}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive("/dashboard/therapist/content")}
                      tooltip={t(
                        "navigation.contentLibrary",
                        "Content library"
                      )}
                    >
                      <Link to="/dashboard/therapist/content">
                        <Library className="size-4" />
                        <span>
                          {t("navigation.contentLibrary", "Content library")}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>
                {t("navigation.clientManagement", "Clients")}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive("/dashboard/therapist/clients")}
                      tooltip={t("navigation.clientManagement")}
                    >
                      <Link to="/dashboard/therapist/clients">
                        <Users className="size-4" />
                        <span>{t("navigation.clientManagement")}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive("/dashboard/therapist/analytics")}
                      tooltip={t("navigation.analytics", "Analytics")}
                    >
                      <Link to="/dashboard/therapist/analytics">
                        <TrendingUp className="size-4" />
                        <span>{t("navigation.analytics", "Analytics")}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {isAdmin && (
              <SidebarGroup>
                <SidebarGroupLabel>
                  {t("navigation.admin", "Admin")}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive("/admin/legal")}
                        tooltip={t("navigation.adminLegal", "Legal Documents")}
                      >
                        <Link to="/admin/legal">
                          <Scale className="size-4" />
                          <span>
                            {t("navigation.adminLegal", "Legal Documents")}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive("/admin/users")}
                        tooltip={t("navigation.adminUsers", "User Management")}
                      >
                        <Link to="/admin/users">
                          <Users className="size-4" />
                          <span>
                            {t("navigation.adminUsers", "User Management")}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </>
        )}

        {/* ── Client Navigation ── */}
        {isClient && (
          <SidebarGroup>
            <SidebarGroupLabel>
              {t("navigation.journey", "Journey")}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={
                      isActive("/dashboard/client/journey") &&
                      !location.pathname.includes("/history")
                    }
                    tooltip={t("navigation.overview", "Overview")}
                  >
                    <Link to="/dashboard/client/journey">
                      <Home className="size-4" />
                      <span>{t("navigation.overview", "Overview")}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive("/dashboard/client/journey/history")}
                    tooltip={t("journey.pastJourneys")}
                  >
                    <Link to="/dashboard/client/journey/history">
                      <History className="size-4" />
                      <span>{t("journey.pastJourneys")}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Footer: compact settings row */}
      <SidebarFooter className="border-t border-sidebar-border pt-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isActive("/profile")}
              tooltip={t("navigation.profile")}
            >
              <Link to="/profile">
                <User className="size-4" />
                <span>{t("navigation.profile")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={t("navigation.signOut")}
              onClick={signOut}
            >
              <LogOut className="size-4" />
              <span>{t("navigation.signOut")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <div className="flex items-center gap-1.5 px-2 py-1 group-data-[collapsible=icon]:hidden">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
